import React, { Fragment, useState, useEffect } from 'react';
import { Row, Col, Card } from 'react-bootstrap';
import { useApiServices } from '../../services/api-endpoints/useApiServices';
import { useSignalR } from '../../services/signalR/SignalRProvider';
import { useLanguage } from '../../services/language/LanguageContext';
import { Link } from 'react-router-dom';
import { DateTimeFormat } from '../../helper/Formatters';
import Pagination from '../../components/pagination';
import ImageViewer from '../../components/image-viewer/ImageViewer';

const AVATAR_COLORS = ['#1e3a5f', '#7c3aed', '#0891b2', '#059669', '#d97706', '#dc2626'];
const getAvatarColor = (str = '') => AVATAR_COLORS[(str.charCodeAt(0) || 0) % AVATAR_COLORS.length];

const UserAvatar = ({ firstName, lastName, image }) => {
    const initials = `${(firstName || '')[0] || ''}${(lastName || '')[0] || ''}`.toUpperCase() || '?';
    const color = getAvatarColor(firstName || '');
    if (image) {
        return (
            <div style={{ width: 32, height: 32, borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}>
                <ImageViewer id={image} width={32} preview={false} />
            </div>
        );
    }
    return (
        <div style={{
            width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
            background: `linear-gradient(135deg, ${color}cc, ${color})`,
            color: '#fff', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: 11, fontWeight: 700,
        }}>
            {initials}
        </div>
    );
};

const RoleBadge = ({ name, id }) => {
    const isAdmin = id === 1;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '3px 10px 3px 7px',
            background: isAdmin ? '#fef3c7' : '#f3f4f6',
            border: `1px solid ${isAdmin ? '#fcd34d' : '#e5e7eb'}`,
            borderRadius: 20, fontSize: 12, fontWeight: 600,
            color: isAdmin ? '#92400e' : '#6b7280',
        }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: isAdmin ? '#f59e0b' : '#9ca3af', flexShrink: 0 }} />
            {name || '-'}
        </span>
    );
};

const Users = () => {
    const { messages } = useSignalR();
    const api = useApiServices();
    const { t } = useLanguage();
    const [data, setData] = useState({});
    const [keyword, setKeyword] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [hoveredRow, setHoveredRow] = useState(null);

    const fetchData = async (page = 1, kw = keyword) => {
        const params = { pageNumber: page, pageSize: 10, keyword: kw || undefined, filterOptions: { isAdmin: true } };
        const { isSuccess, result } = await api.GetUserListAsync(params);
        if (isSuccess) setData(result);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setKeyword(searchInput);
        fetchData(1, searchInput);
    };

    useEffect(() => { fetchData(); }, []);

    useEffect(() => {
        if (!messages?.length) return;
        const last = messages[messages.length - 1];
        if (last && last.type === 'User' && data?.currentPage === 1) fetchData();
    }, [messages]);

    const items = data.items || [];

    return (
        <Fragment>
            <Row>
                <Col sm="12">
                    <Card style={cardStyle}>
                        <Card.Header style={cardHeaderStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={titleDotStyle} />
                                <h5 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1e3a5f' }}>{t('users_title')}</h5>
                                {data.totalItems > 0 && <span style={countBadgeStyle}>{data.totalItems}</span>}
                            </div>
                            <form onSubmit={handleSearch} style={{ display: 'flex', gap: 6 }}>
                                <div style={searchWrapStyle}>
                                    <i className="fa fa-search" style={searchIconStyle} />
                                    <input type="text" placeholder={t('search_users')} value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)} style={searchInputStyle} />
                                </div>
                                {keyword && (
                                    <button type="button" style={clearBtnStyle} onClick={() => { setSearchInput(''); setKeyword(''); fetchData(1, ''); }}>
                                        <i className="fa fa-times" />
                                    </button>
                                )}
                            </form>
                        </Card.Header>
                        <Card.Body style={{ padding: 0 }}>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={tableStyle}>
                                    <thead>
                                        <tr>
                                            {[t('col_user'), t('col_phone'), t('col_email'), t('col_role'), t('col_date')].map(h => (
                                                <th key={h} style={thStyle}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.length === 0 ? (
                                            <tr><td colSpan={5} style={emptyStyle}>
                                                <i className="fa fa-users" style={{ fontSize: 24, marginBottom: 8, display: 'block', opacity: 0.3 }} />
                                                {t('no_data')}
                                            </td></tr>
                                        ) : items.map(row => (
                                            <tr key={row.id}
                                                onMouseEnter={() => setHoveredRow(row.id)}
                                                onMouseLeave={() => setHoveredRow(null)}
                                                style={{ background: hoveredRow === row.id ? '#f9fafb' : '#fff', transition: 'background 0.1s' }}>
                                                <td style={tdStyle}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                        <UserAvatar firstName={row.firstName} lastName={row.lastName} image={row.image} />
                                                        <Link to={`${row.id}`} style={linkStyle}>
                                                            {`${row.firstName || ''} ${row.lastName || ''}`.trim() || '-'}
                                                        </Link>
                                                    </div>
                                                </td>
                                                <td style={tdStyle}>
                                                    <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#374151' }}>{row.phone || '-'}</span>
                                                </td>
                                                <td style={{ ...tdStyle, color: '#6b7280' }}>{row.email || '-'}</td>
                                                <td style={tdStyle}><RoleBadge name={row.userType?.name} id={row.userType?.id} /></td>
                                                <td style={{ ...tdStyle, color: '#9ca3af', fontSize: 12 }}>{DateTimeFormat(row.createDate)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div style={{ padding: '12px 20px' }}>
                                <Pagination currentPage={data.currentPage} totalPages={data.totalPages} onPageChange={(page) => fetchData(page)} />
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Fragment>
    );
};

const cardStyle = { borderRadius: 12, border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' };
const cardHeaderStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, padding: '16px 20px', borderBottom: '1px solid #f3f4f6', background: '#fff', borderRadius: '12px 12px 0 0' };
const titleDotStyle = { width: 10, height: 10, borderRadius: '50%', background: 'linear-gradient(135deg, #1e3a5f, #2d6a9f)', flexShrink: 0 };
const countBadgeStyle = { fontSize: 11, fontWeight: 600, background: '#e0e7ff', color: '#4f46e5', borderRadius: 10, padding: '1px 7px' };
const searchWrapStyle = { position: 'relative', display: 'flex', alignItems: 'center' };
const searchIconStyle = { position: 'absolute', left: 10, color: '#9ca3af', fontSize: 12, pointerEvents: 'none' };
const searchInputStyle = { paddingLeft: 30, paddingRight: 12, paddingTop: 6, paddingBottom: 6, border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none', minWidth: 200, color: '#374151' };
const clearBtnStyle = { padding: '6px 10px', background: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer', fontSize: 12 };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const thStyle = { padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' };
const tdStyle = { padding: '12px 16px', borderBottom: '1px solid #f3f4f6', fontSize: 13, color: '#1f2937', verticalAlign: 'middle' };
const linkStyle = { color: '#1e3a5f', fontWeight: 600, textDecoration: 'none' };
const emptyStyle = { padding: '48px 0', textAlign: 'center', color: '#9ca3af', fontSize: 13, borderBottom: 'none' };

export default Users;
